import {
  useGetApiV1PersonIdAdditionalData,
  useGetApiV1PersonIdAddresses,
  useGetApiV1PersonIdBasicData,
  useGetApiV1PersonIdContactData,
  useGetApiV1PersonIdDocuments,
  useGetApiV1PersonIdEmployeeData,
  useGetApiV1PersonIdFinancialData,
  useGetApiV1PersonIdRoles,
  useGetApiV1PersonIdStudentData,
  useGetApiV1PersonIdTeacherData,
} from '@/shared/api/generated';

import type {
  Person,
  PersonAddress,
  PersonEmployee,
  PersonFinancial,
  PersonStudent,
  PersonTeachingStaff,
} from '../types/person';

// ─── Mappers ─────────────────────────────────────────────────────────────────

function mapAddress(
  raw:
    | {
        id?: number | null;
        street?: string | null;
        zipCode?: string | null;
        placeNumber?: string | null;
        neighborhood?: string | null;
        complement?: string | null;
        countryId?: number | null;
        stateId?: number | null;
        cityId?: number | null;
        stateName?: string | null;
        cityName?: string | null;
      }
    | null
    | undefined,
): PersonAddress | null {
  if (!raw) return null;
  return {
    addressPersonId: raw.id ?? null,
    street: raw.street ?? null,
    zipCode: raw.zipCode ?? null,
    placeNumber: raw.placeNumber ?? null,
    neighborhood: raw.neighborhood ?? null,
    complement: raw.complement ?? null,
    countryId: raw.countryId ?? null,
    stateId: raw.stateId ?? null,
    cityId: raw.cityId ?? null,
    stateName: raw.stateName ?? null,
    cityName: raw.cityName ?? null,
  };
}

function mapStudent(
  raw:
    | {
        studentId?: number | null;
        ra?: string | null;
        curriculum?: string | null;
        codeUsp?: string | null;
        perfilPublic?: boolean | null;
      }
    | null
    | undefined,
): PersonStudent | null {
  if (!raw) return null;
  return {
    id: raw.studentId ?? null,
    ra: raw.ra ?? null,
    curriculum: raw.curriculum ?? null,
    codeUsp: raw.codeUsp ?? null,
    perfilPublic: raw.perfilPublic ?? false,
  };
}

function mapEmployee(
  raw:
    | {
        employeeId?: number | null;
        postId?: number | null;
        codeBadge?: string | null;
        dateStart?: string | null;
        dateFinish?: string | null;
        type?: number | null;
        serviceRequestNotification?: boolean | null;
      }
    | null
    | undefined,
): PersonEmployee | null {
  if (!raw) return null;
  return {
    id: raw.employeeId ?? null,
    postId: raw.postId ?? null,
    codeBadge: raw.codeBadge ?? null,
    dateStart: raw.dateStart ?? null,
    dateFinish: raw.dateFinish ?? null,
    type: raw.type ?? null,
    serviceRequestNotification: raw.serviceRequestNotification ?? false,
  };
}

function mapTeachingStaff(
  raw:
    | {
        teachingStaffId?: number | null;
        observation?: string | null;
        isUspTeaching?: boolean | null;
        codeUSP?: string | null;
        certNumber?: string | null;
        uspContractType?: number | null;
        academicEducation?: number | null;
        certWorkload?: number | null;
        certExpirationDate?: string | null;
        isLattes?: boolean | null;
        lattesUpdateDate?: string | null;
      }
    | null
    | undefined,
): PersonTeachingStaff | null {
  if (!raw) return null;
  return {
    id: raw.teachingStaffId ?? null,
    observation: raw.observation ?? null,
    isUspTeaching: raw.isUspTeaching ?? false,
    codeUSP: raw.codeUSP ?? null,
    certNumber: raw.certNumber ?? null,
    uspContractType: raw.uspContractType ?? null,
    academicEducation: raw.academicEducation ?? null,
    certWorkload: raw.certWorkload ?? null,
    certExpirationDate: raw.certExpirationDate ?? null,
    isLattes: raw.isLattes ?? false,
    lattesUpdateDate: raw.lattesUpdateDate ?? null,
  };
}

function mapFinancial(
  raw:
    | {
        personFinancialId?: number | null;
        bankName?: string | null;
        agency?: string | null;
        checkingAccount?: string | null;
        savingAccount?: string | null;
        paymentMethod?: number | null;
        accountType?: number | null;
      }
    | null
    | undefined,
): PersonFinancial | null {
  if (!raw) return null;
  return {
    id: raw.personFinancialId ?? null,
    bankName: raw.bankName ?? null,
    agency: raw.agency ?? null,
    checkingAccount: raw.checkingAccount ?? null,
    savingAccount: raw.savingAccount ?? null,
    paymentMethod: raw.paymentMethod ?? null,
    accountType: raw.accountType ?? null,
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

const SILENT_QUERY = { retry: 0, throwOnError: false } as const;

/**
 * Hook para carregar os dados de edição de uma pessoa, chamando um endpoint por aba.
 *
 * Dispara todos os endpoints em paralelo: basic-data, contact-data, documents,
 * additional-data, addresses e roles são queries principais (erros afetam o estado
 * de erro da página). financial-data, student-data, employee-data e teacher-data
 * são opcionais — erros são ignorados silenciosamente (ex: 404 quando o perfil
 * não existe para a pessoa).
 *
 * @param personId - ID da pessoa
 * @returns Dados da pessoa mapeados com estado de loading/error
 *
 * @example
 * ```tsx
 * const { person, isLoading, isError } = usePersonEditData(personId);
 * ```
 */
export function usePersonEditData(personId: number) {
  // Queries principais — erros propagam para a página
  const basicData = useGetApiV1PersonIdBasicData(personId);
  const contactData = useGetApiV1PersonIdContactData(personId);
  const documents = useGetApiV1PersonIdDocuments(personId);
  const additionalData = useGetApiV1PersonIdAdditionalData(personId);
  const addresses = useGetApiV1PersonIdAddresses(personId);
  const roles = useGetApiV1PersonIdRoles(personId);

  // Queries opcionais — erros silenciosos (perfis/financeiro podem não existir)
  const financialData = useGetApiV1PersonIdFinancialData(personId, { query: SILENT_QUERY });
  const studentData = useGetApiV1PersonIdStudentData(personId, { query: SILENT_QUERY });
  const employeeData = useGetApiV1PersonIdEmployeeData(personId, { query: SILENT_QUERY });
  const teacherData = useGetApiV1PersonIdTeacherData(personId, { query: SILENT_QUERY });

  // Queries principais: bloqueiam a montagem do formulário
  const isLoading =
    basicData.isLoading ||
    contactData.isLoading ||
    documents.isLoading ||
    additionalData.isLoading ||
    addresses.isLoading ||
    roles.isLoading;

  // Loading por step: cada aba controla seu próprio esqueleto.
  // Queries opcionais ficam fora do isLoading global — o formulário monta
  // assim que os dados principais chegam, e os campos opcionais são populados
  // via setValue quando as queries concluem (evita campos vazios em cache-hit).
  const isLoadingProfiles =
    studentData.isLoading || employeeData.isLoading || teacherData.isLoading;

  const isLoadingFinancial = financialData.isLoading;

  const isError =
    basicData.isError ||
    contactData.isError ||
    documents.isError ||
    additionalData.isError ||
    addresses.isError ||
    roles.isError;

  if (
    !basicData.data ||
    !contactData.data ||
    !documents.data ||
    !additionalData.data ||
    !addresses.data ||
    !roles.data
  ) {
    return { person: null, isLoading, isError, isLoadingProfiles, isLoadingFinancial } as const;
  }

  const basic = basicData.data;
  const contact = contactData.data;
  const docs = documents.data;
  const additional = additionalData.data;
  const rolesData = roles.data;

  // EAddressType: 1 = Personal | 3 = Payment
  const personalAddr = addresses.data.find(a => a.type === 1) ?? null;
  const paymentAddr = addresses.data.find(a => a.type === 3) ?? null;

  const person: Person = {
    // Identificação
    id: basic.id ?? personId,
    code: basic.code ?? '',
    active: true,
    createdAt: '',

    // Dados básicos
    firstName: basic.firstName ?? '',
    lastName: basic.lastName ?? '',
    fullName: `${basic.firstName ?? ''} ${basic.lastName ?? ''}`.trim(),
    alias: basic.alias ?? null,
    email: basic.email ?? '',
    cpf: docs.cpf ?? '',

    // Dados pessoais
    birthDate: basic.birthDate ?? null,
    gender: basic.gender ?? null,
    martialStatus: basic.martialStatus ?? null,
    nationalityId: basic.nationalityId ?? null,
    birthCityId: basic.birthCityId ?? null,
    birthCityName: basic.birthCityName ?? null,
    birthStateId: basic.birthStateId ?? null,
    birthStateName: basic.birthStateName ?? null,
    birthCountryId: basic.birthCountryId ?? null,
    birthCountryName: basic.birthCountryName ?? null,
    mothersName: basic.mothersName ?? null,
    fathersName: basic.fathersName ?? null,
    spouseName: basic.spouseName ?? null,

    // Contato
    celPhone: contact.celPhone ?? '',
    homePhone: contact.homePhone ?? null,
    workPhone: contact.workPhone ?? null,
    receiveSms: contact.receiveSms ?? false,
    receiveMarketing: contact.receiveMarketing ?? false,

    // Currículo e redes
    miniCurriculum: additional.miniCurriculum ?? null,
    urlLinkedin: additional.urlLinkedin ?? null,
    urlLattes: additional.urlLattes ?? null,
    observation: additional.observation ?? null,

    // Documentos
    rg: docs.rg ?? null,
    dispatcherOrgan: docs.dispatcherOrgan ?? null,
    dispatcherOrganState: docs.dispatcherOrganState ?? null,
    dateDispatch: docs.dateDispatch ?? null,
    passportNumber: docs.passportNumber ?? null,
    idCountryNumber: docs.idCountryNumber ?? null,

    // Profissional
    profession: additional.profession ?? null,
    professionId: additional.professionId ?? null,
    company: additional.company ?? null,
    associatedCompanyId: additional.associatedCompanyId ?? null,
    jobPositionId: additional.jobPositionId ?? null,
    otherJobPosition: additional.otherJobPosition ?? null,
    businessSectorId: additional.businessSectorId ?? null,
    otherBusinessSector: additional.otherBusinessSector ?? null,
    serviceTime: additional.serviceTime ?? null,
    experienceLevel: additional.experienceLevel ?? null,
    selfEmployed: additional.selfEmployed ?? false,
    isWorking: additional.isWorking ?? false,

    // Formação acadêmica
    academicEducation: additional.academicEducation ?? null,
    academicInstitutionId: additional.academicInstitutionId ?? null,
    academicCourseName: additional.academicCourseName ?? null,
    completionDate: additional.completionDate ?? null,

    // Perfis
    isStudent: rolesData.isStudent ?? false,
    isMonitor: rolesData.isMonitor ?? false,
    isEmployee: rolesData.isEmployee ?? false,
    isTeacher: rolesData.isTeachingStaff ?? false,
    student: mapStudent(studentData.data),
    employee: mapEmployee(employeeData.data),
    teachingStaff: mapTeachingStaff(teacherData.data),

    // Acessibilidade
    isPCD: additional.isPCD ?? false,
    pcdDescription: additional.pcdDescription ?? null,
    useAccessibilityTool: additional.useAccessibilityTool ?? false,
    accessibilityToolDescription: additional.accessibilityToolDescription ?? null,

    // Endereços
    personalAddress: mapAddress(personalAddr),
    paymentAddress: mapAddress(paymentAddr),

    // Financeiro
    financial: mapFinancial(financialData.data),
  };

  return { person, isLoading, isError, isLoadingProfiles, isLoadingFinancial } as const;
}
